// src/components/modals/CollectPaymentModal.jsx
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

// Strict numeric entry: digits + one decimal point, no e/E/+/-/scientific
// notation (type="number" alone still admits those).
const NUMERIC_KEYS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Home", "End"];
const blockNonNumericKey = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return; // let copy/paste/shortcuts through (paste filtered below)
  if (!NUMERIC_KEYS.includes(e.key)) e.preventDefault();
};
const blockNonNumericPaste = (e) => {
  const text = (e.clipboardData || window.clipboardData).getData("text");
  if (!/^\d*\.?\d*$/.test(text)) e.preventDefault();
};
const numericInputHandlers = { onKeyDown: blockNonNumericKey, onPaste: blockNonNumericPaste };

const CollectPaymentModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  invoice,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const [amount, setAmount] = useState("");
  const [paymentTypeId, setPaymentTypeId] = useState("");
  const [note, setNote] = useState("");
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(false);

  const remaining = parseFloat(invoice?.balance) || 0;
  const currency = invoice?.currency || "";

  // Reset whenever the modal opens for a different invoice
  useEffect(() => {
    if (isOpen && invoice) {
      setAmount(String(parseFloat(invoice.balance) || 0));
      setPaymentTypeId("");
      setNote("");
    }
  }, [isOpen, invoice]);

  // Payment methods (same endpoint the POS invoice modal uses)
  useEffect(() => {
    if (!isOpen || paymentTypes.length) return;
    const fetchPaymentTypes = async () => {
      setLoadingPaymentTypes(true);
      try {
        const token = localStorage.getItem("access_token");
        const base = import.meta.env.VITE_API_URL || "http://localhost:8000";
        const response = await fetch(`${base}/api/v1/payment-types`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });
        if (response.ok) {
          const data = await response.json();
          const list = data?.data || [];
          setPaymentTypes(Array.isArray(list) ? list : []);
        }
      } catch (e) {
        console.warn("Failed to load payment types:", e);
      } finally {
        setLoadingPaymentTypes(false);
      }
    };
    fetchPaymentTypes();
  }, [isOpen, paymentTypes.length]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !isLoading) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen || !invoice) {
    return null;
  }

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const numAmount = parseFloat(amount) || 0;
  const isValid = numAmount > 0 && numAmount <= remaining;

  const modalContent = (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          borderRadius: "16px",
          padding: "32px",
          maxWidth: "440px",
          width: "100%",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          fontFamily: '"Red Hat Display", sans-serif',
          direction: isRTL ? "rtl" : "ltr",
        }}
        onClick={(e) => e.stopPropagation()}
        dir={i18n.dir()}
        lang={i18n.language}
      >
        {/* Header */}
        <div style={{ marginBottom: "20px", textAlign: isRTL ? "right" : "center" }}>
          <div
            style={{
              width: "48px",
              height: "48px",
              backgroundColor: "#E6FAF5",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px auto",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#00D4B3"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
          </div>

          <h3
            style={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#1F2937",
              margin: "0 0 8px 0",
            }}
          >
            {t("billing.collect.title", "Collect Payment")}
          </h3>

          <p
            style={{
              fontSize: "15px",
              color: "#374151",
              fontWeight: 500,
              margin: "0 0 4px 0",
              lineHeight: "1.5",
              wordBreak: "break-word",
            }}
          >
            {invoice.invoice_number || `#${invoice.id}`}
          </p>

          <p
            style={{
              fontSize: "14px",
              color: "#6B7280",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            {t("billing.collect.remaining", "Remaining balance: {{amount}} {{currency}}", {
              amount: remaining.toFixed(2),
              currency,
            })}
          </p>
        </div>

        {/* Amount */}
        <div style={{ marginBottom: "16px" }}>
          <label
            htmlFor="collect-amount"
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: 500,
              color: "#374151",
              marginBottom: "8px",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t("billing.collect.amountLabel", "Amount")}
          </label>
          <input
            id="collect-amount"
            type="number"
            min="0.01"
            max={remaining}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={isLoading}
            {...numericInputHandlers}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 14px",
              border: numAmount > remaining ? "1px solid #EF4444" : "1px solid #D1D5DB",
              borderRadius: "8px",
              fontSize: "14px",
              color: "#111827",
              background: "#FFFFFF",
              fontFamily: '"Red Hat Display", sans-serif',
            }}
          />
          {numAmount > remaining && (
            <p style={{ fontSize: "12px", color: "#DC2626", margin: "4px 0 0", textAlign: isRTL ? "right" : "left" }}>
              {t("billing.collect.overpayWarn", "Amount exceeds the remaining balance.")}
            </p>
          )}
        </div>

        {/* Payment method */}
        <div style={{ marginBottom: "16px" }}>
          <label
            htmlFor="collect-method"
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: 500,
              color: "#374151",
              marginBottom: "8px",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t("billing.collect.methodLabel", "Payment method")}
          </label>
          <select
            id="collect-method"
            value={paymentTypeId}
            onChange={(e) => setPaymentTypeId(e.target.value)}
            disabled={isLoading || loadingPaymentTypes}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 14px",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              fontSize: "14px",
              color: "#111827",
              background: "#FFFFFF",
              fontFamily: '"Red Hat Display", sans-serif',
            }}
          >
            <option value="">
              {loadingPaymentTypes
                ? t("billing.collect.loadingMethods", "Loading methods...")
                : t("billing.collect.methodPlaceholder", "Select a method (optional)")}
            </option>
            {paymentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.name}
              </option>
            ))}
          </select>
        </div>

        {/* Note */}
        <div style={{ marginBottom: "24px" }}>
          <label
            htmlFor="collect-note"
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: 500,
              color: "#374151",
              marginBottom: "8px",
              textAlign: isRTL ? "right" : "left",
            }}
          >
            {t("billing.collect.noteLabel", "Note (optional)")}
          </label>
          <textarea
            id="collect-note"
            rows={2}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={isLoading}
            placeholder={t("billing.collect.notePlaceholder", "e.g. Paid in cash at reception")}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 14px",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              fontSize: "14px",
              color: "#111827",
              background: "#FFFFFF",
              fontFamily: '"Red Hat Display", sans-serif',
              resize: "vertical",
              lineHeight: "1.5",
            }}
          />
        </div>

        {/* Actions */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: "12px 24px",
              backgroundColor: "transparent",
              color: isLoading ? "#9CA3AF" : "#374151",
              border: "1px solid #D1D5DB",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: 500,
              cursor: isLoading ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              fontFamily: '"Red Hat Display", sans-serif',
              minWidth: "80px",
            }}
          >
            {t("billing.collect.cancel", "Cancel")}
          </button>

          <button
            onClick={() =>
              onConfirm({
                amount: numAmount,
                payment_type_id: paymentTypeId ? parseInt(paymentTypeId, 10) : undefined,
                notes: note.trim() || undefined,
              })
            }
            disabled={isLoading || !isValid}
            style={{
              padding: "12px 24px",
              backgroundColor: isLoading || !isValid ? "#99E5D8" : "#00D4B3",
              color: "#FFFFFF",
              border: "none",
              borderRadius: "8px",
              fontSize: "16px",
              fontWeight: 600,
              cursor: isLoading || !isValid ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              fontFamily: '"Red Hat Display", sans-serif',
              minWidth: "80px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            {isLoading && (
              <div
                style={{
                  width: "16px",
                  height: "16px",
                  border: "2px solid #FFFFFF",
                  borderTop: "2px solid transparent",
                  borderRadius: "50%",
                  animation: "billing-spin 1s linear infinite",
                }}
              />
            )}
            {t("billing.collect.confirm", "Confirm Payment")}
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes billing-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CollectPaymentModal;
